import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { throwFromResponse } from '../lib/apiErrorMessage';
import type { AdminMemberSummary, AdminUsersQueryParams, AdminUsersPage } from './useAdminUsersQuery';

export type { AdminMemberSummary, AdminUsersQueryParams, AdminUsersPage };

const buildQuery = (params: AdminUsersQueryParams): string => {
  const q = new URLSearchParams();
  if (params.keyword) q.set('keyword', params.keyword);
  if (params.status) q.set('status', params.status);
  if (params.mallCode) q.set('mallCode', params.mallCode);
  if (params.memberType) q.set('memberType', params.memberType);
  if (params.nationality) q.set('nationality', params.nationality);
  if (params.grade) q.set('grade', params.grade);
  if (params.joinedFrom) q.set('joinedFrom', params.joinedFrom);
  if (params.joinedTo) q.set('joinedTo', params.joinedTo);
  if (params.lastLoginFrom) q.set('lastLoginFrom', params.lastLoginFrom);
  if (params.lastLoginTo) q.set('lastLoginTo', params.lastLoginTo);
  if (params.emailOptIn != null) q.set('emailOptIn', String(params.emailOptIn));
  if (params.smsOptIn != null) q.set('smsOptIn', String(params.smsOptIn));
  if (params.marketingOptIn != null) q.set('marketingOptIn', String(params.marketingOptIn));
  if (params.mileageMin != null) q.set('mileageMin', String(params.mileageMin));
  if (params.mileageMax != null) q.set('mileageMax', String(params.mileageMax));
  if (params.page != null) q.set('page', String(Math.max(0, params.page - 1)));
  if (params.size != null) q.set('size', String(params.size));
  if (params.unmask) q.set('unmask', 'true');
  return q.toString();
};

interface AdminMemberListEnvelope {
  success: boolean;
  data?: {
    items?: AdminMemberSummary[];
    content?: AdminMemberSummary[];
    meta?: { total: number; page: number; size: number };
    totalElements?: number;
    number?: number;
    size?: number;
  } | null;
}

export const userListQueryKey = (params: AdminUsersQueryParams) =>
  ['admin', 'users', 'list', params] as const;

export const useUserListQuery = (params: AdminUsersQueryParams, unmask = true) => {
  const paramsWithUnmask: AdminUsersQueryParams = { ...params, unmask };
  return useQuery<AdminUsersPage>({
    queryKey: userListQueryKey(paramsWithUnmask),
    queryFn: async () => {
      const qs = buildQuery(paramsWithUnmask);
      const res = await adminFetch(`/api/admin/v1/users${qs ? '?' + qs : ''}`);
      if (!res.ok) await throwFromResponse(res, '회원 목록을 불러오지 못했습니다.');
      const env = (await res.json()) as AdminMemberListEnvelope;
      const d = env.data;
      const items = d?.items ?? d?.content ?? [];
      const meta = d?.meta ?? {
        total: d?.totalElements ?? items.length,
        page: d?.number ?? 0,
        size: d?.size ?? 20,
      };
      return { items, meta };
    },
  });
}
